// Sector Packages (M13) — bir framework_version'a PINNED varyant.
// Exclusion array'leri + override JSONB'leri code-based. Snapshot resolved view ile donar.

import { z } from "zod";
import { SectorCode } from "../constants/sectors.js";
import { QuestionType } from "./questions.js";

export const SectorPackageStatus = z.enum(["draft", "published", "archived"]);
export type SectorPackageStatus = z.infer<typeof SectorPackageStatus>;

// Override sınırları: 4 metin alanı (label/sectionPrompt + label/aiContext).
// Yapısal değişiklik (type/options/required) override edilemez.
export const SectionOverrideSchema = z
  .object({
    label: z.string().min(1).max(200).optional(),
    sectionPrompt: z.string().max(5000).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: "Override en az 1 alan içermeli",
  });
export type SectionOverride = z.infer<typeof SectionOverrideSchema>;

export const QuestionOverrideSchema = z
  .object({
    label: z.string().min(1).max(300).optional(),
    aiContext: z.string().max(3000).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: "Override en az 1 alan içermeli",
  });
export type QuestionOverride = z.infer<typeof QuestionOverrideSchema>;

// Override map'leri: key code-based, value override shape.
// section_overrides: { "<sectionCode>": SectionOverride }
// question_overrides: { "<sectionCode>.<questionCode>": QuestionOverride }
export const SectionOverridesMapSchema = z.record(z.string(), SectionOverrideSchema);
export const QuestionOverridesMapSchema = z.record(z.string(), QuestionOverrideSchema);

// Slug: URL-safe, kebab-case (TR karakteri yok).
export const SectorPackageSlugSchema = z
  .string()
  .min(2, "Slug en az 2 karakter")
  .max(80, "Slug en fazla 80 karakter")
  .regex(/^[a-z0-9-]+$/, "Slug sadece küçük harf, rakam ve - içerebilir");

// Liste/detail summary — admin UI'da row + sağ panel header.
export const SectorPackageSummarySchema = z.object({
  id: z.string().uuid(),
  parentFrameworkVersionId: z.string().uuid(),
  parentFrameworkId: z.string().uuid(),
  parentFrameworkName: z.string(),
  parentVersionNumber: z.string(),
  parentVersionStatus: z.enum(["draft", "published", "archived"]),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  status: SectorPackageStatus,
  excludedSectionCodes: z.array(z.string()),
  excludedQuestionCodes: z.array(z.string()),
  sectionOverrides: SectionOverridesMapSchema,
  questionOverrides: QuestionOverridesMapSchema,
  // M13: outdated bayrağı — parent framework'ün daha yeni published version'ı varsa true.
  isOutdated: z.boolean(),
  createdBy: z.string().uuid(),
  createdByName: z.string().nullable(),
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SectorPackageSummary = z.infer<typeof SectorPackageSummarySchema>;

export const SectorPackagesListResponseSchema = z.object({
  packages: z.array(SectorPackageSummarySchema),
});
export type SectorPackagesListResponse = z.infer<typeof SectorPackagesListResponseSchema>;

// Resolved view — snapshot için ve admin tree editor için.
// SectionView + QuestionView snapshot'a yazılan shape ile aynı.
export const SectorPackageSectionViewSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  order: z.number().int(),
  sectionPrompt: z.string(),
});
export type SectorPackageSectionView = z.infer<typeof SectorPackageSectionViewSchema>;

export const SectorPackageQuestionViewSchema = z.object({
  id: z.string().uuid(),
  sectionId: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  helperText: z.string().nullable(),
  type: QuestionType,
  options: z.unknown(),
  required: z.boolean(),
  enabled: z.boolean(),
  priorYearHintDisabled: z.boolean(),
  allowEvidence: z.boolean(),
  allowNote: z.boolean(),
  aiContext: z.string().nullable(),
  order: z.number().int(),
});
export type SectorPackageQuestionView = z.infer<typeof SectorPackageQuestionViewSchema>;

export const SectorPackageOrphanReportSchema = z.object({
  excludedSectionCodes: z.array(z.string()),
  excludedQuestionCodes: z.array(z.string()),
  sectionOverrideKeys: z.array(z.string()),
  questionOverrideKeys: z.array(z.string()),
});
export type SectorPackageOrphanReport = z.infer<typeof SectorPackageOrphanReportSchema>;

export const ResolvedSectorPackageSchema = z.object({
  parentVersionId: z.string().uuid(),
  parentVersionNumber: z.string(),
  sections: z.array(SectorPackageSectionViewSchema),
  questions: z.array(SectorPackageQuestionViewSchema),
  orphans: SectorPackageOrphanReportSchema,
});
export type ResolvedSectorPackage = z.infer<typeof ResolvedSectorPackageSchema>;

// CRUD requests
export const CreateSectorPackageRequestSchema = z
  .object({
    parentFrameworkVersionId: z.string().uuid(),
    name: z.string().min(2, "Paket adı en az 2 karakter").max(200),
    slug: SectorPackageSlugSchema,
    description: z.string().max(1000).optional(),
  })
  .strict();
export type CreateSectorPackageRequest = z.infer<typeof CreateSectorPackageRequestSchema>;

// Update — draft'ta yapılabilen değişiklikler.
// expectedUpdatedAt: optimistic locking (mevcut framework-versions pattern).
export const UpdateSectorPackageRequestSchema = z
  .object({
    expectedUpdatedAt: z.string().datetime(),
    name: z.string().min(2).max(200).optional(),
    description: z.string().max(1000).nullable().optional(),
    excludedSectionCodes: z.array(z.string()).optional(),
    excludedQuestionCodes: z.array(z.string()).optional(),
    sectionOverrides: SectionOverridesMapSchema.optional(),
    questionOverrides: QuestionOverridesMapSchema.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).filter((k) => k !== "expectedUpdatedAt").length > 0, {
    message: "En az bir alan güncellenmeli",
  });
export type UpdateSectorPackageRequest = z.infer<typeof UpdateSectorPackageRequestSchema>;

// Upgrade — paket'i parent'ın yeni version'ına remap eder.
export const UpgradeSectorPackageRequestSchema = z
  .object({
    newParentFrameworkVersionId: z.string().uuid(),
    // dryRun=true sadece orphan raporu döner, DB değişmez.
    dryRun: z.boolean().default(false),
  })
  .strict();
export type UpgradeSectorPackageRequest = z.infer<typeof UpgradeSectorPackageRequestSchema>;

export const UpgradeSectorPackageResponseSchema = z.object({
  orphans: SectorPackageOrphanReportSchema,
  applied: z.boolean(), // dryRun=true → false; gerçek upgrade → true
  newParentVersionId: z.string().uuid().nullable(),
  newParentVersionNumber: z.string().nullable(),
});
export type UpgradeSectorPackageResponse = z.infer<typeof UpgradeSectorPackageResponseSchema>;

// ProjectNew wizard endpoint response — frameworkVersionId'ye bağlı published paketler.
export const PublishedSectorPackagesResponseSchema = z.object({
  packages: z.array(SectorPackageSummarySchema),
});
export type PublishedSectorPackagesResponse = z.infer<typeof PublishedSectorPackagesResponseSchema>;

// Sektör paketi orphan-edit eligibility (published + projeye atanmamış).
export const SectorPackageEditabilityResponseSchema = z.object({
  editable: z.boolean(),
  projectCount: z.number().int().nonnegative(),
  status: z.enum(["draft", "published", "archived"]),
});
export type SectorPackageEditabilityResponse = z.infer<
  typeof SectorPackageEditabilityResponseSchema
>;

// Feature B (M13 S7): framework version edit/delete eligibility.
export const FrameworkVersionEditabilityResponseSchema = z.object({
  editable: z.boolean(),
  reasons: z.array(z.enum(["has_projects", "has_sector_packages"])),
  projectCount: z.number().int().nonnegative(),
  sectorPackageCount: z.number().int().nonnegative(),
});
export type FrameworkVersionEditabilityResponse = z.infer<
  typeof FrameworkVersionEditabilityResponseSchema
>;

// ---------------------------------------------------------------------------
// Yayınlarken paket türetme (2026-10-01) — kit `SEK-<KOD>` blokları
// ---------------------------------------------------------------------------

/** Bir `SEK-*` bölümü ve ondan türetilecek paketin önizlemesi. */
export const SectorBlockSchema = z.object({
  /** Bölüm kodu — `SEK-` ile başlar. */
  sectionCode: z.string(),
  /** Bölüm adı (editörde yazan). */
  sectionLabel: z.string(),
  /** `SEK-` sonrası gövdenin sözlük karşılığı; sözlükte yoksa null. */
  sectorCode: SectorCode.nullable(),
  /** Türetilecek paketin adı — sözlükte varsa `SECTOR_LABELS[kod]`, yoksa bölüm adı. */
  packageName: z.string(),
});
export type SectorBlock = z.infer<typeof SectorBlockSchema>;

export const SectorBlocksResponseSchema = z.object({ blocks: z.array(SectorBlockSchema) });
export type SectorBlocksResponse = z.infer<typeof SectorBlocksResponseSchema>;

/** Türetme sonucu — `publishVersion` yanıtında satır başına bir kayıt. */
export const DerivedSectorPackageSchema = z.object({
  sectionCode: z.string(),
  name: z.string(),
  /** Türetilen (ya da zaten var olan) paketin id'si; hata dalında null. */
  packageId: z.string().uuid().nullable(),
  /**
   * `skipped_permission`: aktörde `sector_packages.create`/`.publish` yok — yayın
   * geçti, paket açılmadı. `failed`: türetme tx'i geri alındı (taslak/slug kalmadı).
   */
  status: z.enum(["derived", "failed", "skipped_permission"]),
  /** `failed`/`skipped_permission` için kısa sebep; `derived`'da null. */
  message: z.string().nullable(),
});
export type DerivedSectorPackage = z.infer<typeof DerivedSectorPackageSchema>;
